// @ts-nocheck
/**
 * Semantic validation for ElementDefinition.overview (Overview Recipes).
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue, makePassResult } from '../../diagnostics';
import type { DataRef, ElementDefinition, TemplateDocument, ValueSource } from '../../types/canonical';
import {
  OVERVIEW_COLUMN_SPLITS,
  OVERVIEW_HERO_SIZES,
  OVERVIEW_IMAGE_FITS,
  OVERVIEW_RECIPE_IDS,
  type OverviewRecipeId,
  type OverviewSlotRole,
} from '../../types/overview';
import { OVERVIEW_RECIPE_CATALOG, overviewRecipeSlotIds } from '../../overview/recipeCatalog';

const RECIPE_ID_SET = new Set<string>(OVERVIEW_RECIPE_IDS);
const COLUMN_SPLIT_SET = new Set<string>(OVERVIEW_COLUMN_SPLITS);
const FIT_SET = new Set<string>(OVERVIEW_IMAGE_FITS);
const SIZE_SET = new Set<string>(OVERVIEW_HERO_SIZES);

function isValueSource(value: unknown): value is ValueSource<unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const kind = (value as { kind?: unknown }).kind;
  if (kind === 'static') return 'value' in (value as object);
  if (kind === 'binding') {
    const ref = (value as { ref?: unknown }).ref;
    return ref !== null && typeof ref === 'object' && typeof (ref as { kind?: unknown }).kind === 'string';
  }
  if (kind === 'expression') {
    return typeof (value as { expressionId?: unknown }).expressionId === 'string';
  }
  return false;
}

function dataRefFromSource(source: ValueSource<unknown>): DataRef | null {
  if (source.kind === 'binding') return source.ref;
  return null;
}

export function checkElementOverview(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();

  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i] as ElementDefinition;
    const overview = element.overview;
    if (overview === undefined) continue;

    const pathBase = `elements[${i}].overview`;

    if (overview === null || typeof overview !== 'object' || Array.isArray(overview)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_overview',
        message: `Element '${element.id}' overview must be an object`,
        path: pathBase,
      });
      continue;
    }

    if (!RECIPE_ID_SET.has(overview.recipeId)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_overview_recipe',
        message: `Element '${element.id}' overview.recipeId '${String(overview.recipeId)}' is not a known recipe`,
        path: `${pathBase}.recipeId`,
      });
      continue;
    }

    const recipeId = overview.recipeId as OverviewRecipeId;
    const recipe = OVERVIEW_RECIPE_CATALOG[recipeId];
    const allowedSlots = overviewRecipeSlotIds(recipeId);

    if (!COLUMN_SPLIT_SET.has(overview.columnSplit)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_overview_column_split',
        message: `Element '${element.id}' overview.columnSplit must be auto, stack, or split`,
        path: `${pathBase}.columnSplit`,
      });
    }

    if (!recipe.supportsColumnSplit && overview.columnSplit === 'split') {
      result = addIssue(result, {
        severity: 'warning',
        code: 'semantic.overview_split_unsupported',
        message: `Element '${element.id}' overview recipe '${recipeId}' does not support column split; split is ignored`,
        path: `${pathBase}.columnSplit`,
      });
    }

    const hero = overview.hero;
    if (hero === null || typeof hero !== 'object' || Array.isArray(hero)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_overview_hero',
        message: `Element '${element.id}' overview.hero must be an object`,
        path: `${pathBase}.hero`,
      });
    } else {
      if (!FIT_SET.has(hero.fit)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_overview_hero_fit',
          message: `Element '${element.id}' overview.hero.fit must be contain, cover, or fill`,
          path: `${pathBase}.hero.fit`,
        });
      }
      if (hero.size !== undefined && !SIZE_SET.has(hero.size)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_overview_hero_size',
          message: `Element '${element.id}' overview.hero.size must be sm, md, or lg`,
          path: `${pathBase}.hero.size`,
        });
      }
      const source = hero.source;
      if (source === null || typeof source !== 'object' || Array.isArray(source)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_overview_hero_source',
          message: `Element '${element.id}' overview.hero.source must be an object`,
          path: `${pathBase}.hero.source`,
        });
      } else if (source.kind === 'itemImage') {
        if (
          source.preferredIndex !== undefined &&
          (!Number.isInteger(source.preferredIndex) || source.preferredIndex < 0)
        ) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_overview_hero_index',
            message: `Element '${element.id}' overview.hero.source.preferredIndex must be a non-negative integer`,
            path: `${pathBase}.hero.source.preferredIndex`,
          });
        }
      } else if (source.kind === 'templateAsset') {
        if (typeof source.assetId !== 'string' || source.assetId.trim() === '') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_overview_hero_asset',
            message: `Element '${element.id}' overview.hero.source.assetId must be a non-empty string`,
            path: `${pathBase}.hero.source.assetId`,
          });
        }
      } else if (source.kind !== 'none') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_overview_hero_source_kind',
          message: `Element '${element.id}' overview.hero.source.kind must be itemImage, templateAsset, or none`,
          path: `${pathBase}.hero.source.kind`,
        });
      }

      if (recipe.hero.forceNone && source?.kind !== 'none') {
        result = addIssue(result, {
          severity: 'warning',
          code: 'semantic.overview_hero_forced_none',
          message: `Element '${element.id}' overview recipe '${recipeId}' forces hero.none`,
          path: `${pathBase}.hero.source`,
        });
      }
    }

    const slots = overview.slots;
    if (slots === null || typeof slots !== 'object' || Array.isArray(slots)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_overview_slots',
        message: `Element '${element.id}' overview.slots must be an object`,
        path: `${pathBase}.slots`,
      });
      continue;
    }

    for (const [slotId, assignment] of Object.entries(slots)) {
      const slotPath = `${pathBase}.slots.${slotId}`;
      if (!allowedSlots.has(slotId)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.unknown_overview_slot',
          message: `Element '${element.id}' overview slot '${slotId}' is not valid for recipe '${recipeId}'`,
          path: slotPath,
        });
        continue;
      }
      if (assignment === null || typeof assignment !== 'object' || Array.isArray(assignment)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_overview_slot',
          message: `Element '${element.id}' overview slot '${slotId}' must be an object`,
          path: slotPath,
        });
        continue;
      }
      if (!isValueSource(assignment.source)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_overview_slot_source',
          message: `Element '${element.id}' overview slot '${slotId}' source must be a ValueSource`,
          path: `${slotPath}.source`,
        });
        continue;
      }

      const ref = dataRefFromSource(assignment.source);
      if (ref?.kind === 'localState') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.overview_local_state_disallowed',
          message: `Element '${element.id}' overview slot '${slotId}' may not bind localState`,
          path: `${slotPath}.source`,
        });
      }
    }

    if (recipeId !== 'titleTable') {
      for (const [slotId] of Object.entries(slots)) {
        const slotSpec = recipe.slots.find((s) => s.id === slotId);
        if (slotSpec && slotSpec.role === ('table' as OverviewSlotRole)) {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.overview_table_slot_recipe',
            message: `Element '${element.id}' table slots are only valid on titleTable`,
            path: `${pathBase}.slots.${slotId}`,
          });
        }
      }
    }
  }

  return result;
}
